import React, { Component } from "react";
import { ContentWrapper, Icon, PhoneInput, Table } from "components";
import { Row, Col, Input, notification, DatePicker, Button, Select, Drawer, Popconfirm } from "antd";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import Strings from "utils/strings";
import { push } from "connected-react-router";
import moment from "moment";
import { LANGUAGES, translate } from "utils/utils";
import "./styles.scss";

// Editable input component that maintains local state to prevent losing focus
class EditableNumberInput extends React.Component<any, any> {
	constructor(props: any) {
		super(props);
		this.state = {
			value: props.value != null ? props.value : "",
			isFocused: false
		};
	}

	componentDidUpdate(prevProps: any) {
		// Only update local state if the prop value changed and we're not focused
		if (prevProps.value !== this.props.value && !this.state.isFocused) {
			this.setState({ value: this.props.value != null ? this.props.value : "" });
		}
	}

	render() {
		const { onChange, onBlur, placeholder, style, step, ...restProps } = this.props;
		const { value } = this.state;

		return (
			<Input
				{...restProps}
				type="number"
				step={step || "0.01"}
				value={value}
				placeholder={placeholder}
				style={style}
				onFocus={() => {
					this.setState({ isFocused: true });
				}}
				onChange={(e: any) => {
					const inputValue = e.target.value;
					this.setState({ value: inputValue });
					// Don't call onChange immediately to prevent re-render
				}}
				onBlur={(e: any) => {
					const inputValue = e.target.value;
					this.setState({ isFocused: false });
					if (onBlur) {
						onBlur(e);
					}
					// Sync with parent on blur
					if (onChange && inputValue !== this.props.value) {
						onChange(inputValue);
					}
				}}
			/>
		);
	}
}

// Editable text input that keeps local state while typing to avoid losing focus on re-render
class EditableTextInput extends React.Component<any, any> {
	constructor(props: any) {
		super(props);
		this.state = {
			value: props.value != null ? props.value : "",
			isFocused: false
		};
	}

	componentDidUpdate(prevProps: any) {
		if (prevProps.value !== this.props.value && !this.state.isFocused) {
			this.setState({ value: this.props.value != null ? this.props.value : "" });
		}
	}

	render() {
		const { onChange, onBlur, placeholder, ...restProps } = this.props;
		const { value } = this.state;

		return (
			<Input
				{...restProps}
				value={value}
				placeholder={placeholder}
				onFocus={() => this.setState({ isFocused: true })}
				onChange={(e: any) => this.setState({ value: e.target.value })}
				onBlur={(e: any) => {
					const inputValue = e.target.value;
					this.setState({ isFocused: false });
					if (onBlur) onBlur(e);
					if (onChange && inputValue !== this.props.value) {
						onChange(inputValue);
					}
				}}
			/>
		);
	}
}

class WorkshopPurchaseDetail extends Component<any, any> {
	phoneValidationInput: any;
	constructor(props: any) {
		super(props);

		this.state = {
			purchase: null,
			hasUnsavedFields: false,
			isNew: props.match?.params?.id === "new",
			date: null,
			supplier: null,
			invoiceNumber: "",
			notes: "",
			items: [],
			parts: [],
			suppliers: [],
			total: 0,
			showSupplierDrawer: false,
			showPartDrawer: false,
			tempSupplier: null,
			tempPart: null,
			partIndex: undefined,
			language: "pt"
		};
	}

	componentDidMount(): void {
		delayedDispatch(
			setBreadcrumb(() => {
				return {
					locations: [
						{
							text: Strings.workshop.purchases,
							route: "/workshop/purchases",
							icon: "shopping-cart"
						},
						{
							text: this.state.isNew
								? Strings.workshop.newPurchase
								: Strings.workshop.purchase
						}
					],
					actions: [
						{
							type: "button",
							text: Strings.generic.save,
							onClick: () => this.submitPurchase(),
							disabled: !this.state.hasUnsavedFields,
							className: this.state.hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							isSave: true
						}
					]
				};
			})
		);

		this.getData();
		this.getParts();
		this.getSuppliers();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;

		dispatch(updateCrumb());
	}

	async getData() {
		const { isNew } = this.state;
		const { dispatch, match } = this.props;

		if (isNew) {
			return dispatch(setTitle(Strings.workshop.newPurchase));
		}

		dispatch(setLoader(true));

		try {
			const response = await API.get({
				url: Endpoints.uriWorkshopPurchases(match?.params?.id)
			});

			if (response.ok) {
				const purchase = response.data?.results?.purchase;
				dispatch(setTitle(Strings.workshop.purchase));
				this.setState({
					purchase,
					date: purchase?.date ? moment(purchase.date) : null,
					supplier: purchase?.supplier?._id || purchase?.supplier || null,
					invoiceNumber: purchase?.invoiceNumber || "",
					notes: purchase?.notes || "",
					items: (purchase?.items || []).map((item: any) => ({
						...item,
						type: item.type || "part",
						part: item.part?._id ? String(item.part._id) : (item.part ? String(item.part) : null),
						description: item.description || ""
					})),
					total: purchase?.total || 0
				});
			}
		} catch (err) {
			console.log("API Error", err);
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async getParts() {
		try {
			const response = await API.get({
				url: Endpoints.uriWorkshopParts()
			});

			if (response.ok) {
				this.setState({ parts: response.data?.results?.part || [] });
			}
		} catch (err) {
			console.log("API Error", err);
		}
	}

	async getSuppliers() {
		try {
			const response = await API.get({
				url: Endpoints.uriSuppliers()
			});

			if (response.ok) {
				this.setState({ suppliers: response.data?.results?.supplier || [] });
			}
		} catch (err) {
			console.log("API Error", err);
		}
	}

	calculateTotal() {
		const { items } = this.state;
		let total = 0;
		items.forEach((item: any) => {
			if (item.quantity && item.unitPrice) {
				total += item.quantity * item.unitPrice;
			}
		});
		this.setState({ total, hasUnsavedFields: true });
	}

	addItem() {
		const { items } = this.state;
		this.setState(
			{
				items: [
					...items,
					{
						type: "part",
						part: null,
						description: "",
						quantity: 1,
						unitPrice: null,
						total: 0
					}
				],
				hasUnsavedFields: true
			},
			() => this.calculateTotal()
		);
	}

	removeItem(index: number) {
		const { items } = this.state;
		const newItems = [...items];
		newItems.splice(index, 1);
		this.setState({ items: newItems, hasUnsavedFields: true }, () => this.calculateTotal());
	}

	updateItem(index: number, field: string, value: any) {
		const { items } = this.state;
		const processedValue = field === "part" && value ? String(value) : value;
		
		const newItems = [...items];
		newItems[index] = {
			...newItems[index],
			[field]: processedValue
		};
		
		if (field === "type") {
			if (processedValue === "service") {
				newItems[index].part = null;
			} else {
				newItems[index].description = "";
			}
		}
		
		if (field === "quantity" || field === "unitPrice") {
			const quantity = newItems[index].quantity != null ? parseFloat(newItems[index].quantity) || 0 : 0;
			const unitPrice = newItems[index].unitPrice != null ? parseFloat(newItems[index].unitPrice) || 0 : 0;
			newItems[index].total = quantity * unitPrice;
		}
		
		this.setState({ items: newItems, hasUnsavedFields: true }, () => this.calculateTotal());
	}

	async submitPurchase() {
		const { date, supplier, invoiceNumber, notes, items, isNew } = this.state;
		const { dispatch, match } = this.props;

		// Validate date
		if (!date) {
			return notification.warn({
				message: Strings.workshop.purchase,
				description: "A data é obrigatória.",
				placement: "bottomRight",
				duration: 5
			});
		}

		// Validate items
		if (!items || items.length === 0) {
			return notification.warn({
				message: Strings.workshop.purchase,
				description: "Deve adicionar pelo menos um item.",
				placement: "bottomRight",
				duration: 5
			});
		}

		// Validate each item
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			const lineNum = i + 1;
			if ((item.type || "part") === "part") {
				if (!item.part) {
					return notification.warn({
						message: Strings.workshop.purchase,
						description: `A linha ${lineNum} deve ter uma peça selecionada.`,
						placement: "bottomRight",
						duration: 5
					});
				}
			} else {
				if (!(item.description || "").trim()) {
					return notification.warn({
						message: Strings.workshop.purchase,
						description: `A linha ${lineNum} deve ter uma descrição.`,
						placement: "bottomRight",
						duration: 5
					});
				}
			}
			if (item.quantity == null || item.quantity === "") {
				return notification.warn({
					message: Strings.workshop.purchase,
					description: `A linha ${lineNum} deve ter uma quantidade preenchida.`,
					placement: "bottomRight",
					duration: 5
				});
			}
			if (item.unitPrice == null && item.unitPrice !== 0) {
				return notification.warn({
					message: Strings.workshop.purchase,
					description: `A linha ${lineNum} deve ter um preço unitário.`,
					placement: "bottomRight",
					duration: 5
				});
			}
		}

		dispatch(setLoader(true));

		try {
			const request = !isNew ? API.put : API.post;
			const response = await request({
				url: Endpoints.uriWorkshopPurchases((!isNew && match?.params?.id) || ""),
				data: {
					date: date.format("YYYY-MM-DD"),
					supplier: supplier || undefined,
					invoiceNumber: invoiceNumber || undefined,
					notes: notes || undefined,
					items: items.map((item: any) => ({
						type: item.type || "part",
						part: item.type === "part" ? item.part : undefined,
						description: item.type === "service" ? (item.description || "").trim() : undefined,
						quantity: item.quantity,
						unitPrice: item.unitPrice != null ? item.unitPrice : 0,
						total: item.total || 0
					})),
					total: this.state.total
				}
			});

			if (response.ok && response.data.results?.purchase) {
				if (isNew) {
					return dispatch(push("/workshop/purchases"));
				}

				const purchase = response.data.results.purchase;
				this.setState({
					hasUnsavedFields: false,
					purchase,
					date: purchase?.date ? moment(purchase.date) : null,
					supplier: purchase?.supplier?._id || purchase?.supplier || null,
					invoiceNumber: purchase?.invoiceNumber || "",
					notes: purchase?.notes || "",
					items: (purchase?.items || []).map((item: any) => ({
						...item,
						type: item.type || "part",
						part: item.part?._id ? String(item.part._id) : (item.part ? String(item.part) : null),
						description: item.description || ""
					})),
					total: purchase?.total || 0
				});

				notification.success({
					message: Strings.workshop.purchase,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderInfo() {
		const { date, supplier, invoiceNumber, notes, suppliers } = this.state;

		return (
			<ContentWrapper extraStyle={{ padding: 20 }}>
				<div className="WorksheetTitle" style={{ paddingBottom: 30 }}>
					<Icon name="document" />
					<p>{Strings.worksheets.generalInfo}</p>
				</div>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="date" className="InputLabel --label-required">
							{Strings.workshop.date}
						</label>
						<DatePicker
							id="date"
							style={{ width: "100%" }}
							value={date}
							onChange={(date: any) => {
								this.setState({ date, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="supplier" className="InputLabel">
							{Strings.workshop.supplier}
						</label>
						<Select
							id="supplier"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.supplier}
							showSearch
							allowClear
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={supplier || null}
							onChange={(value: any) => {
								this.setState({ supplier: value, hasUnsavedFields: true });
							}}
							dropdownRender={(menu: any) => (
								<div>
									{menu}
									<div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
										<Button
											type="link"
											style={{ width: "100%" }}
											onClick={() => this.setState({ showSupplierDrawer: true, tempSupplier: {} })}
										>
											+ {Strings.suppliers.new}
										</Button>
									</div>
								</div>
							)}
						>
							{suppliers
								.filter((s: any) => s.isActive !== false)
								.map((s: any) => (
									<Select.Option key={s._id} value={s._id}>
										{s.name || "-"}
									</Select.Option>
								))}
						</Select>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="invoiceNumber" className="InputLabel">
							{Strings.workshop.invoiceNumber}
						</label>
						<Input
							id="invoiceNumber"
							placeholder={Strings.workshop.invoiceNumber}
							value={invoiceNumber || ""}
							onChange={(event: any) => {
								this.setState({ invoiceNumber: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="notes" className="InputLabel">
							{Strings.fields.notes}
						</label>
						<Input.TextArea
							id="notes"
							placeholder={Strings.fields.notes}
							value={notes || ""}
							rows={4}
							onChange={(event: any) => {
								this.setState({ notes: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
				</Row>
			</ContentWrapper>
		);
	}

	renderItems() {
		const { items, total, parts } = this.state;

		return (
			<ContentWrapper>
				<Table
					title={{
						icon: "tools",
						title: Strings.workshop.items
					}}
					data={items.map((item: any, index: number) => ({ ...item, _index: index }))}
					columns={[
						{
							Header: Strings.workshop.type,
							id: "type",
							accessor: (row: any) => row.type || "part",
							Cell: ({ row }: any) => {
								const index = row.original._index;
								const type = row.original.type || "part";
								return (
									<Select
										style={{ width: "100%" }}
										value={type}
										onChange={(val) => this.updateItem(index, "type", val)}
									>
										<Select.Option value="part">{Strings.workshop.part}</Select.Option>
										<Select.Option value="service">{Strings.workshop.service}</Select.Option>
									</Select>
								);
							}
						},
						{
							Header: Strings.workshop.part + " / " + Strings.workshop.description,
							id: "partOrDescription",
							width: 300,
							accessor: (row: any) => {
								if (row.type === "service") return row.description || "";
								const partId = row.part?._id || row.part || null;
								return partId ? String(partId) : "";
							},
							Cell: ({ row }: any) => {
								const item = row.original;
								const index = item._index;
								const type = item.type || "part";
								if (type === "service") {
									return (
										<EditableTextInput
											value={item.description || ""}
											placeholder={Strings.workshop.description}
											onChange={(value: string) => this.updateItem(index, "description", value)}
										/>
									);
								}
								const partId = item.part?._id || item.part || null;
								const partIdString = partId ? String(partId) : undefined;
								return (
									<Select
										style={{ width: "100%" }}
										value={partIdString}
										placeholder={Strings.workshop.selectPart}
										showSearch
										optionLabelProp="label"
										dropdownStyle={{ minWidth: "400px" }}
										filterOption={(input: any, option: any) => {
											const searchText = input.toLowerCase();
											const partNumber = option.props?.partNumber?.toLowerCase() || "";
											const partName = option.props?.partName?.toLowerCase() || "";
											return partNumber.indexOf(searchText) >= 0 || partName.indexOf(searchText) >= 0;
										}}
										onChange={(val) => this.updateItem(index, "part", val)}
										dropdownRender={(menu: any) => (
											<div>
												{menu}
												<div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
													<Button
														type="link"
														style={{ width: "100%" }}
														onClick={() => this.setState({ showPartDrawer: true, tempPart: {}, partIndex: index })}
													>
														+ {Strings.workshop.newPart}
													</Button>
												</div>
											</div>
										)}
									>
										{parts
											.filter((p: any) => p.isActive !== false)
											.map((part: any) => {
												const partName = translate(part.name) || "-";
												const partNumber = part.partNumber || "";
												return (
													<Select.Option 
														key={String(part._id)} 
														value={String(part._id)}
														label={partName}
														partNumber={partNumber}
														partName={partName}
													>
														{partNumber ? `${partNumber} - ${partName}` : partName}
													</Select.Option>
												);
											})}
									</Select>
								);
							}
						},
						{
							Header: Strings.workshop.unit,
							id: "unit",
							accessor: (row: any) => {
								if (row.type === "service") return "-";
								const partId = row.part || null;
								const selectedPart = parts.find((p: any) => String(p._id) === String(partId));
								return selectedPart?.unit || "-";
							}
						},
						{
							Header: Strings.workshop.quantity,
							id: "quantity",
							align: "right",
							accessor: (row: any) => row.quantity != null ? row.quantity : "",
							Cell: ({ row }: any) => {
								const quantity = row.original.quantity != null ? row.original.quantity : "";
								const index = row.original._index;
								return (
									<EditableNumberInput
										min={0}
										step="0.01"
										value={quantity}
										placeholder="0"
										style={{ textAlign: "right" }}
										onChange={(inputValue: any) => {
											if (inputValue === "" || inputValue === null) {
												this.updateItem(index, "quantity", null);
											} else {
												const numValue = parseFloat(inputValue);
												if (!isNaN(numValue)) {
													this.updateItem(index, "quantity", numValue);
												}
											}
										}}
									/>
								);
							}
						},
						{
							Header: Strings.workshop.unitPrice,
							id: "unitPrice",
							align: "right",
							accessor: (row: any) => row.unitPrice != null ? row.unitPrice : "",
							Cell: ({ row }: any) => {
								const unitPrice = row.original.unitPrice != null ? row.original.unitPrice : null;
								const index = row.original._index;
								return (
									<EditableNumberInput
										step="0.01"
										value={unitPrice != null ? unitPrice : ""}
										placeholder="0.00"
										style={{ textAlign: "right" }}
										onChange={(inputValue: any) => {
											if (inputValue === "" || inputValue === null) {
												this.updateItem(index, "unitPrice", null);
											} else {
												const numValue = parseFloat(inputValue);
												if (!isNaN(numValue)) {
													// Format to 2 decimal places
													const formattedValue = parseFloat(numValue.toFixed(2));
													this.updateItem(index, "unitPrice", formattedValue);
												}
											}
										}}
									/>
								);
							}
						},
						{
							Header: Strings.workshop.total,
							id: "total",
							align: "right",
							accessor: (row: any) => `${(row.total || 0).toFixed(2)} €`
						},
						{
							Header: Strings.generic.actions,
							id: "actions",
							align: "right",
							accessor: () => "",
							Cell: ({ row }: any) => {
								const index = row.original._index;
								return (
									<Popconfirm
										title="Tem a certeza?"
										okText="Remover"
										cancelText="Cancelar"
										okType="danger"
										placement="topRight"
										onConfirm={() => this.removeItem(index)}
									>
										<div className="ActionOption" style={{ cursor: "pointer", display: "inline-block" }}>
											<Icon name="trash" />
										</div>
									</Popconfirm>
								);
							}
						}
					]}
					fullPage
					paginated={false}
					add={{
						onClick: () => this.addItem()
					}}
				/>
				<div style={{ marginTop: 20, textAlign: "right" }}>
					<label className="InputLabel" style={{ fontSize: 18, fontWeight: 700 }}>
						{Strings.workshop.total}: {total.toFixed(2)} €
					</label>
				</div>
			</ContentWrapper>
		);
	}

	render(): JSX.Element {
		const { isNew } = this.state;

		return (
			<React.Fragment>
				<Helmet>
					<title>
						{Strings.workshop.purchase} -{" "}
						{isNew ? Strings.workshop.newPurchase : Strings.workshop.purchase}
					</title>
					<meta name="description" content="Description of Workshop Purchase" />
				</Helmet>
				<div className="WorksheetScreen" style={{ paddingBottom: 80 }}>
					{this.renderInfo()}
					{this.renderItems()}
				</div>
				{this.renderSupplierDrawer()}
				{this.renderPartDrawer()}
			</React.Fragment>
		);
	}

	async submitSupplier() {
		const { tempSupplier } = this.state;
		const { dispatch } = this.props;

		if (!tempSupplier?.name?.trim()) {
			return notification.warn({
				message: Strings.suppliers.supplier,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const response = await API.post({
				url: Endpoints.uriSuppliers(),
				data: { ...tempSupplier }
			});

			if (response.ok && response.data.results?.supplier) {
				await this.getSuppliers();
				this.setState({
					showSupplierDrawer: false,
					tempSupplier: null,
					supplier: response.data.results.supplier._id,
					hasUnsavedFields: true
				});

				notification.success({
					message: Strings.suppliers.supplier,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async submitPart() {
		const { tempPart, partIndex } = this.state;
		const { dispatch } = this.props;

		if (!tempPart?.partNumber?.trim() || !translate(tempPart?.name)) {
			return notification.warn({
				message: Strings.workshop.part,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const response = await API.post({
				url: Endpoints.uriWorkshopParts(),
				data: {
					partNumber: tempPart.partNumber?.toUpperCase() || "",
					name: tempPart.name,
					instructions: tempPart.instructions || {},
					unit: tempPart.unit || "un",
					preferredSupplier: tempPart.preferredSupplier || undefined
				}
			});

			if (response.ok && response.data.results?.part) {
				const newPart = response.data.results.part;
				await this.getParts();
				
				if (partIndex !== undefined && newPart?._id) {
					this.updateItem(partIndex, "part", String(newPart._id));
				}
				
				this.setState({
					showPartDrawer: false,
					tempPart: null,
					partIndex: undefined,
					hasUnsavedFields: true
				});

				notification.success({
					message: Strings.workshop.part,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err: any) {
			const errorMessage = err?.message || err?.toString() || Strings.serverErrors.wentWrong;
			notification.error({
				message: Strings.serverErrors.title,
				description: errorMessage,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderSupplierDrawer() {
		const { showSupplierDrawer, tempSupplier } = this.state;
		const cnt = "PT";

		return (
			<Drawer
				title={
					<div className="SidebarTitleContainer">
						<Icon name="user-group" />
						<p>{Strings.suppliers.new}</p>
					</div>
				}
				footer={
					<div className="SidebarFooterContainer">
						<button
							type="button"
							className="SidebarFooterButton --button-confirm"
							onClick={() => this.submitSupplier()}
						>
							{Strings.generic.confirm}
						</button>
						<button
							type="button"
							className="SidebarFooterButton --button-cancel"
							onClick={() => this.setState({ showSupplierDrawer: false, tempSupplier: null })}
						>
							{Strings.generic.cancel}
						</button>
					</div>
				}
				placement="right"
				width={400}
				onClose={() => this.setState({ tempSupplier: null, showSupplierDrawer: false })}
				visible={showSupplierDrawer}
			>
				<Row gutter={[0, 10]}>
					<Col xs={24}>
						<label htmlFor="supplier_name" className="InputLabel --label-required">
							{Strings.fields.name}
						</label>
						<Input
							id="supplier_name"
							value={tempSupplier?.name || ""}
							placeholder={Strings.fields.name}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempSupplier: {
										...state.tempSupplier,
										name: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="supplier_contact" className="InputLabel">
							{Strings.fields.name} ({Strings.suppliers.contacts})
						</label>
						<Input
							id="supplier_contact"
							value={tempSupplier?.contact || ""}
							placeholder={Strings.fields.name}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempSupplier: {
										...state.tempSupplier,
										contact: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="supplier_email" className="InputLabel">
							{Strings.fields.email}
						</label>
						<Input
							id="supplier_email"
							type="email"
							value={tempSupplier?.email || ""}
							placeholder={Strings.fields.email}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempSupplier: {
										...state.tempSupplier,
										email: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="supplier_phone" className="InputLabel">
							{Strings.fields.phone}
						</label>
						{Boolean(cnt) && (
							<PhoneInput
								ref={(ref: any) => {
									this.phoneValidationInput = ref;
								}}
								defaultCountry={cnt && cnt.toLowerCase()}
								value={tempSupplier?.phone || ""}
								inputClass="input-phone"
								onChange={(event: any) => {
									this.setState((state: any) => ({
										tempSupplier: {
											...state.tempSupplier,
											phone: event
										}
									}));
								}}
							/>
						)}
					</Col>
					<Col xs={24}>
						<label htmlFor="supplier_address" className="InputLabel">
							{Strings.fields.address}
						</label>
						<Input.TextArea
							id="supplier_address"
							value={tempSupplier?.address || ""}
							placeholder={Strings.fields.address}
							rows={3}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempSupplier: {
										...state.tempSupplier,
										address: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="supplier_notes" className="InputLabel">
							{Strings.fields.notes}
						</label>
						<Input.TextArea
							id="supplier_notes"
							value={tempSupplier?.notes || ""}
							placeholder={Strings.fields.notes}
							rows={4}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempSupplier: {
										...state.tempSupplier,
										notes: value
									}
								}));
							}}
						/>
					</Col>
				</Row>
			</Drawer>
		);
	}

	renderPartDrawer() {
		const { showPartDrawer, tempPart, language, suppliers } = this.state;

		const unitOptions = [
			{ value: 'un', label: 'Unidade (un)' },
			{ value: 'l', label: 'Litro (l)' },
			{ value: 'kg', label: 'Quilograma (kg)' },
			{ value: 'm', label: 'Metro (m)' },
			{ value: 'm2', label: 'Metro Quadrado (m²)' },
			{ value: 'ml', label: 'Metro Linear (ml)' }
		];

		return (
			<Drawer
				title={
					<div className="SidebarTitleContainer">
						<Icon name="tools" />
						<p>{Strings.workshop.newPart}</p>
					</div>
				}
				footer={
					<div className="SidebarFooterContainer">
						<button
							type="button"
							className="SidebarFooterButton --button-confirm"
							onClick={() => this.submitPart()}
						>
							{Strings.generic.confirm}
						</button>
						<button
							type="button"
							className="SidebarFooterButton --button-cancel"
							onClick={() => this.setState({ showPartDrawer: false, tempPart: null, partIndex: undefined })}
						>
							{Strings.generic.cancel}
						</button>
					</div>
				}
				placement="right"
				width={400}
				onClose={() => this.setState({ tempPart: null, showPartDrawer: false, partIndex: undefined })}
				visible={showPartDrawer}
			>
				<div className="DrawerLanguageContainer">
					<Select
						className="DrawerLanguageDropdown"
						optionFilterProp="children"
						onChange={(lang) => this.setState({ language: lang })}
						filterOption={(input: any, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						value={language}
					>
						{LANGUAGES.map((lang: any, index: number) => {
							return (
								<Select.Option key={`select_option_${lang}_${index}`} value={lang.value}>
									{lang.label}
								</Select.Option>
							);
						})}
					</Select>
				</div>
				<Row gutter={[0, 10]}>
					<Col xs={24}>
						<label htmlFor="part_number" className="InputLabel --label-required">
							{Strings.workshop.partNumber}
						</label>
						<Input
							id="part_number"
							value={tempPart?.partNumber || ""}
							placeholder={Strings.workshop.partNumber}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										partNumber: value
									}
								}));
							}}
							onBlur={(event: any) => {
								const value = event.target.value?.toUpperCase() || "";
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										partNumber: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_unit" className="InputLabel --label-required">
							{Strings.workshop.unit}
						</label>
						<Select
							id="part_unit"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.unit}
							value={tempPart?.unit || 'un'}
							onChange={(value: any) => {
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										unit: value
									}
								}));
							}}
						>
							{unitOptions.map((option) => (
								<Select.Option key={option.value} value={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_name" className="InputLabel --label-required">
							{Strings.workshop.name}
						</label>
						<Input
							id="part_name"
							value={tempPart?.name?.[language] || ""}
							placeholder={Strings.workshop.name}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										name: { ...state.tempPart?.name, [language]: value }
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_instructions" className="InputLabel">
							{Strings.workshop.instructions}
						</label>
						<Input.TextArea
							id="part_instructions"
							value={tempPart?.instructions?.[language] || ""}
							placeholder={Strings.workshop.instructions}
							rows={4}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										instructions: { ...state.tempPart?.instructions, [language]: value }
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_preferredSupplier" className="InputLabel">
							{Strings.workshop.preferredSupplier}
						</label>
						<Select
							id="part_preferredSupplier"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.preferredSupplier}
							showSearch
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={tempPart?.preferredSupplier || null}
							onChange={(value: any) => {
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										preferredSupplier: value
									}
								}));
							}}
							allowClear
						>
							{suppliers
								.filter((s: any) => s.isActive !== false)
								.map((supplier: any) => (
									<Select.Option key={supplier._id} value={supplier._id}>
										{supplier.name || "-"}
									</Select.Option>
								))}
						</Select>
					</Col>
				</Row>
			</Drawer>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language
});

export default connect(mapStateToProps)(WorkshopPurchaseDetail);

